import { SignUpGuardProvider } from '@app/hooks/SignUpGuardContext';
import { withIaCode } from '@app/pages/shop/categoryConfig';
import {
  Box,
  constants,
  PageSection,
  Stack,
  Text,
} from '@redbubble/design-system';
import { ErrorBoundary } from '@redbubble/next/error-boundary';
import { defineMessages, FormattedMessage } from '@redbubble/next/intl';
import dynamic from 'next/dynamic';
import { NextPage } from 'next/types';
import { useMemo } from 'react';
import { TrustSignalsGroup } from '../../../i/components/TrustSignalsGroup';
import type { CategorySearchResultsPageProps } from '../../types';
import { L2Carousel } from '../L2Carousel';
import { L2PillsList } from '../L2PillsList';
import styles from './CategoryPage.module.scss';

const messages = defineMessages({
  moreCategories: { defaultMessage: 'More Categories' },
});

const SignUpGuard = dynamic(
  () =>
    import(
      /* webpackChunkName: "SignUpGuard" */
      '../../../../components/SignUpGuard/SignUpGuard'
    ).then(({ SignUpGuard: Component }) => Component),
  { ssr: false, loading: () => null }
);

export const CategoryPage: NextPage<CategorySearchResultsPageProps> = ({
  config,
  carouselData,
  iaCode,
  iaCodeLabel,
}) => {
  const resultsBySlug = useMemo(
    () => Object.fromEntries(carouselData.map((d) => [d.slug, d.results])),
    [carouselData]
  );

  const activeL2Categories = useMemo(
    () => config.l2Categories.filter((l2) => l2.active),
    [config.l2Categories]
  );
  const remainingL2Categories = useMemo(
    () => config.l2Categories.filter((l2) => !l2.active),
    [config.l2Categories]
  );

  return (
    <SignUpGuardProvider>
      <SignUpGuard />
      <Box element="main" className={styles.page} paddingTop="l">
        <PageSection size={constants.MEDIUM}>
          <Stack spacing="l">
            <Text element="h1" type="display2">
              {iaCodeLabel ? (
                <>
                  <FormattedMessage {...config.attributiveTitle} />{' '}
                  {iaCodeLabel}
                </>
              ) : (
                <FormattedMessage {...config.title} />
              )}
            </Text>
            <L2PillsList l2Categories={activeL2Categories} iaCode={iaCode} />
            <Stack spacing="xl">
              {activeL2Categories.map((l2) => (
                <L2Carousel
                  key={l2.slug}
                  title={l2.title}
                  results={resultsBySlug[l2.slug] ?? []}
                  seeAllPath={withIaCode(l2.seeAllPath, iaCode)}
                  slug={l2.slug}
                />
              ))}
            </Stack>
            {remainingL2Categories.length > 0 && (
              <Text element="h3" type="display5">
                <FormattedMessage {...messages.moreCategories} />
              </Text>
            )}
            <L2PillsList
              l2Categories={remainingL2Categories}
              iaCode={iaCode}
              testId="more-categories-pills-list"
            />
            <ErrorBoundary>
              <TrustSignalsGroup />
            </ErrorBoundary>
          </Stack>
        </PageSection>
      </Box>
    </SignUpGuardProvider>
  );
};
